import { z } from 'zod';

/*
 * Shared between the browser and the server, so the same rule produces the same
 * message on both sides. The server validates regardless — a client-side check
 * is a courtesy to the person typing, never the enforcement.
 *
 * reference/data-model.md §1.
 */

export const ROLES = ['super_admin', 'admin', 'employee', 'client'] as const;
export type Role = (typeof ROLES)[number];

export const roleSchema = z.enum(ROLES);

/**
 * The two roles that reach the admin portal.
 *
 * **`super_admin` is not a tier above `admin`; it is the same job plus four
 * things** (business-rules.md §2). Anywhere the question is "does this person
 * work in the office", both belong — which includes every `role = 'admin'`
 * query that finds somebody to notify or assign work to. A super admin who
 * stopped receiving the leads notification because a query named one role
 * would be a silent hole, so this constant exists to make that impossible to
 * write by accident.
 */
export const OFFICE_ROLES = ['super_admin', 'admin'] as const;
export type OfficeRole = (typeof OFFICE_ROLES)[number];

/** True for both admin roles — the check that means "works in the office". */
export function isOfficeRole(role: Role): role is OfficeRole {
  return role === 'admin' || role === 'super_admin';
}

/** Display labels. Never render the raw value — CLAUDE.md §4, defect P5. */
export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super admin',
  admin: 'Admin',
  employee: 'Employee',
  client: 'Client',
};

/** better-auth's username plugin normalises to lowercase and allows these characters. */
export const usernameSchema = z
  .string()
  .trim()
  .min(3, 'Must be at least 3 characters')
  .max(64, 'Must be 64 characters or fewer')
  .regex(/^[a-zA-Z0-9._]+$/, 'Letters, numbers, dots and underscores only');

/*
 * 12, not 8. These accounts hold customer home addresses and the notes about
 * how to get into them. Matches minPasswordLength in src/server/lib/auth.ts —
 * a shorter rule here would be rejected by the server after the user typed it.
 */
export const passwordSchema = z
  .string()
  .min(12, 'Must be at least 12 characters')
  .max(128, 'Must be 128 characters or fewer');

export const emailSchema = z.email('Enter a valid email address').max(255);

/**
 * The rule that closes P9.
 *
 * The prototype has live accounts whose email column holds the literal strings
 * `client` and `employee`. Those people cannot reset their own password and
 * cannot be contacted. Two halves to the fix: an email, if stored, must be a
 * real one — and an account that logs in with a username instead needs a
 * `recoveryEmail`, so there is always somewhere a reset link can go.
 */
export const contactableRefinement = {
  check: (value: { email?: string | null; recoveryEmail?: string | null }): boolean =>
    Boolean(value.email ?? value.recoveryEmail),
  message: 'Add a recovery email — without one this account can never be reset or contacted',
  path: ['recoveryEmail'] as const,
};

export const createUserSchema = z
  .object({
    name: z.string().trim().min(1, 'Required').max(255),
    email: emailSchema.optional(),
    username: usernameSchema.optional(),
    recoveryEmail: emailSchema.optional(),
    role: roleSchema,
    password: passwordSchema,
  })
  // A login has to be *something*. Without this an account could be created
  // that nobody, including its owner, can ever sign in to.
  .refine((value) => Boolean(value.email ?? value.username), {
    message: 'Give the account an email address or a username to sign in with',
    path: ['email'],
  })
  .refine(contactableRefinement.check, {
    message: contactableRefinement.message,
    path: [...contactableRefinement.path],
  });

export type CreateUserInput = z.infer<typeof createUserSchema>;

/** Sign-in accepts either identifier, exactly as the prototype does. */
export const signInSchema = z.object({
  identifier: z.string().trim().min(1, 'Enter your email address or username'),
  password: z.string().min(1, 'Enter your password'),
  rememberMe: z.boolean().optional(),
});

export type SignInInput = z.infer<typeof signInSchema>;

/** Whether an identifier should be sent to the email endpoint or the username one. */
export function isEmailIdentifier(identifier: string): boolean {
  return z.email().safeParse(identifier.trim()).success;
}

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export const resetPasswordSchema = z
  .object({
    token: z.string().min(1),
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((value) => value.password === value.confirmPassword, {
    message: 'Both passwords must match',
    path: ['confirmPassword'],
  });

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((value) => value.newPassword === value.confirmPassword, {
    message: 'Both passwords must match',
    path: ['confirmPassword'],
  });

/** Where each role lands after signing in. /launch resolves this server-side. */
export const HOME_PATH_BY_ROLE: Record<Role, string> = {
  // The same portal. A super admin is an admin with four extra permissions,
  // not a different destination.
  super_admin: '/admin',
  admin: '/admin',
  employee: '/employee',
  client: '/portal',
};
